import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Camera, Check, ImagePlus, Loader2, Trash2, X } from 'lucide-react';
import { AVATAR_ACCEPTED_TYPES, AVATAR_MAX_BYTES } from '../constants/profileConfig';
import './AvatarDialog.css';

const formatSize = (bytes) =>
    bytes >= 1024 * 1024 ? `${(bytes / (1024 * 1024)).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;

const MAX_MB = Math.round(AVATAR_MAX_BYTES / (1024 * 1024));

/**
 * The "Profile photo" dialog: a large round preview, a button (or drag and drop) to choose a
 * photo, and Save / Cancel. When a photo is already saved it also offers "Remove photo", which
 * asks once before deleting.
 *
 * Nothing is sent until Save (or Remove) is pressed. Esc, the X button, Cancel and a click
 * outside close it, but not while a request is running.
 *
 * @param {{ profile: object, upload: ReturnType<typeof import('../hooks/useAvatarUpload').useAvatarUpload>, onClose: () => void }} props
 */
const AvatarDialog = ({ profile, upload, onClose }) => {
    const inputRef = useRef(null);
    const [confirmingRemoval, setConfirmingRemoval] = useState(false);
    const [failedSource, setFailedSource] = useState('');
    const [dragging, setDragging] = useState(false);

    const { busy, cancel } = upload;
    const source = upload.previewUrl || profile.avatarUrl;
    const showImage = Boolean(source) && failedSource !== source;
    const canRemove = Boolean(profile.avatarUrl) && !upload.hasSelection;

    const close = useCallback(() => {
        if (busy) return;
        cancel(); // drops a chosen photo and any error message
        onClose();
    }, [busy, cancel, onClose]);

    // Esc closes; the page behind does not scroll while the dialog is open.
    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key === 'Escape') close();
        };
        document.addEventListener('keydown', handleKeyDown);
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
            document.body.style.overflow = previousOverflow;
        };
    }, [close]);

    const openPicker = () => inputRef.current?.click();

    const handlePick = (event) => {
        upload.selectFile(event.target.files?.[0]);
        event.target.value = ''; // lets the same file be chosen again
    };

    const handleDrop = (event) => {
        event.preventDefault();
        setDragging(false);
        if (busy || confirmingRemoval) return;
        upload.selectFile(event.dataTransfer?.files?.[0]);
    };

    const handleSave = async () => {
        if (await upload.save()) onClose();
    };

    const handleRemove = async () => {
        if (await upload.remove()) onClose();
    };

    const keepPhoto = () => {
        setConfirmingRemoval(false);
        cancel(); // clears a leftover error message
    };

    return createPortal(
        <div
            className="prof-photo-overlay"
            onMouseDown={(event) => {
                if (event.target === event.currentTarget) close();
            }}
        >
            <div className="prof-photo-dialog" role="dialog" aria-modal="true" aria-labelledby="prof-photo-title">
                <div className="prof-photo-header">
                    <h2 id="prof-photo-title">{confirmingRemoval ? 'Remove photo' : 'Profile photo'}</h2>
                    <button type="button" className="prof-photo-close" onClick={close} disabled={busy} aria-label="Close">
                        <X size={18} aria-hidden="true" />
                    </button>
                </div>

                <div className="prof-photo-body">
                    <div
                        className={`prof-photo-stage${dragging ? ' dragging' : ''}`}
                        onDragOver={(event) => { event.preventDefault(); if (!busy && !confirmingRemoval) setDragging(true); }}
                        onDragLeave={() => setDragging(false)}
                        onDrop={handleDrop}
                    >
                        <div className={`prof-photo-circle${upload.hasSelection ? ' is-new' : ''}`}>
                            {showImage ? (
                                <img
                                    src={source}
                                    alt={upload.hasSelection ? 'New profile photo preview' : `${profile.fullName} profile`}
                                    referrerPolicy="no-referrer"
                                    onError={() => setFailedSource(source)}
                                />
                            ) : (
                                <span className="prof-photo-initials dont-translate" aria-hidden="true">{profile.initials}</span>
                            )}
                            {busy && (
                                <span className="prof-photo-busy" aria-hidden="true">
                                    <Loader2 size={30} className="prof-spin" />
                                </span>
                            )}
                        </div>

                        {!confirmingRemoval && (
                            <button
                                type="button"
                                className="prof-photo-camera"
                                onClick={openPicker}
                                disabled={busy}
                                aria-label="Choose a photo"
                                title="Choose a photo"
                            >
                                <Camera size={17} aria-hidden="true" />
                            </button>
                        )}
                    </div>

                    {confirmingRemoval ? (
                        <div className="prof-photo-text">
                            <p className="prof-photo-lead">Remove your profile photo?</p>
                            <p className="prof-photo-hint">Your initials will be shown instead. You can add a new photo any time.</p>
                        </div>
                    ) : (
                        <div className="prof-photo-text">
                            {upload.hasSelection ? (
                                <p className="prof-photo-lead prof-photo-file" title={upload.fileName}>
                                    <span className="prof-photo-file-name">{upload.fileName}</span>
                                    <span className="prof-photo-file-size">{formatSize(upload.fileSize)}</span>
                                </p>
                            ) : (
                                <p className="prof-photo-lead">Drag a photo here or choose one from your device</p>
                            )}

                            <button type="button" className="prof-btn prof-btn-outline prof-btn-small" onClick={openPicker} disabled={busy} autoFocus>
                                <ImagePlus size={15} aria-hidden="true" />
                                {upload.hasSelection ? 'Choose a different photo' : 'Choose photo'}
                            </button>

                            <p className="prof-photo-hint">
                                JPG, PNG or WEBP, up to {MAX_MB} MB. Your photo is cropped to a square from the centre.
                            </p>
                        </div>
                    )}

                    {upload.error && <div className="prof-photo-error" role="alert">{upload.error}</div>}

                    <input
                        ref={inputRef}
                        type="file"
                        accept={AVATAR_ACCEPTED_TYPES.join(',')}
                        onChange={handlePick}
                        hidden
                    />
                </div>

                <div className="prof-photo-footer">
                    {confirmingRemoval ? (
                        <>
                            <span className="prof-photo-footer-spacer" />
                            <button type="button" className="prof-btn prof-btn-ghost" onClick={keepPhoto} disabled={busy}>
                                Keep photo
                            </button>
                            <button type="button" className="prof-btn prof-btn-danger" onClick={handleRemove} disabled={busy}>
                                {upload.removing ? <Loader2 size={16} className="prof-spin" aria-hidden="true" /> : <Trash2 size={16} aria-hidden="true" />}
                                {upload.removing ? 'Removing...' : 'Remove'}
                            </button>
                        </>
                    ) : (
                        <>
                            {canRemove ? (
                                <button
                                    type="button"
                                    className="prof-btn prof-btn-danger"
                                    onClick={() => setConfirmingRemoval(true)}
                                    disabled={busy}
                                >
                                    <Trash2 size={16} aria-hidden="true" />
                                    Remove photo
                                </button>
                            ) : (
                                <span className="prof-photo-footer-spacer" />
                            )}
                            <button type="button" className="prof-btn prof-btn-ghost" onClick={close} disabled={busy}>
                                Cancel
                            </button>
                            <button type="button" className="prof-btn prof-btn-primary" onClick={handleSave} disabled={busy || !upload.hasSelection}>
                                {upload.saving ? <Loader2 size={16} className="prof-spin" aria-hidden="true" /> : <Check size={16} aria-hidden="true" />}
                                {upload.saving ? 'Saving...' : 'Save photo'}
                            </button>
                        </>
                    )}
                </div>
            </div>
        </div>,
        document.body
    );
};

export default AvatarDialog;
